import { useCallback, useEffect, useRef, useState } from "react"

export const FLEET_VERTICAL_OPTIONS = [
  "trucking",
  "intercity_bus",
  "two_wheeler_delivery",
] as const

export type FleetVertical = (typeof FLEET_VERTICAL_OPTIONS)[number]

export const FLEET_ONBOARDING_MODE_OPTIONS = ["import", "manual"] as const

export type FleetOnboardingMode = (typeof FLEET_ONBOARDING_MODE_OPTIONS)[number]

export const FLEET_ONBOARDING_STEP_OPTIONS = [
  "overview",
  "organization",
  "import",
  "manual",
  "review",
] as const

export type FleetOnboardingStep = (typeof FLEET_ONBOARDING_STEP_OPTIONS)[number]

export interface FleetOnboardingSearch {
  batchCode?: string
  mode?: FleetOnboardingMode
  organizationId?: string
  runId?: string
  step?: FleetOnboardingStep
  vertical?: FleetVertical
}

export interface FleetOperatingUnitDraft {
  city: string
  code: string
  escalationPhone: string
  id: string
  isPrimary: boolean
  managerName: string
  managerPhone: string
  name: string
  region: string
  type: string
}

export interface FleetManualAssetDraft {
  assetNumber: string
  id: string
  notes: string
  operatingUnitId: string
  primaryOperatorName: string
  primaryOperatorPhone: string
  routeOrZone: string
  secondaryOperatorName: string
  secondaryOperatorPhone: string
}

export interface FleetImportBridgeDraft {
  assetCount: number
  assignmentCount: number
  blockingErrors: string[]
  dryRunCreated: number
  dryRunFailed: number
  dryRunRows: Array<{
    error?: string | null
    plateNumber?: string | null
    rowIndex: number
    status: string
    tag?: string | null
  }>
  dryRunTotal: number
  dryRunUpdated: number
  fileName: string
  headers: string[]
  lastDryRunAt: string | null
  operatingUnitHintCount: number
  peopleCount: number
  sourceRows: Array<Record<string, string>>
  warnings: string[]
}

export interface FleetMasterPersistenceDraft {
  assignmentCount: number
  assetCount: number
  lastPersistedAt: string | null
  onboardingMode: FleetOnboardingMode
  organizationId: string
  peopleCount: number
  runId?: string | null
  unitCount: number
  vertical: FleetVertical
}

export type FleetKitTemplateKey =
  | "trucking_standard"
  | "intercity_bus_passenger"
  | "two_wheeler_delivery_shift"

export interface FleetKitTemplateSlot {
  label: string
  scanFlow: string
  slotKey: string
  visibility: "private" | "public"
}

export interface FleetKitDraft {
  assetNumber: string
  assetTypeLabel: string
  operatingUnitLabel: string
  operatorLabel: string
  routeOrZone: string
  slots: Array<
    FleetKitTemplateSlot & {
      previewCode: string
    }
  >
}

export interface FleetKitIssuanceDraft {
  activationMode: "system"
  batchCode: string
  batchLabel: string
  kits: FleetKitDraft[]
  preparedAt: string | null
  templateKey: FleetKitTemplateKey
  templateLabel: string
  totalAssets: number
  totalStickers: number
  vertical: FleetVertical
}

export interface FleetOnboardingDraft {
  activeOrganizationId?: string | null
  activeRunId?: string | null
  adminEmail: string
  adminName: string
  adminPhone: string
  importBridge: FleetImportBridgeDraft | null
  kitIssuance: FleetKitIssuanceDraft | null
  legalName: string
  manualAssetCount: string
  manualAssets: FleetManualAssetDraft[]
  masterPersistence: FleetMasterPersistenceDraft | null
  operatingRegions: string
  organizationName: string
  preferredDriverLanguage: string
  preferredPublicLanguage: string
  primaryCity: string
  primaryCountry: string
  selectedMode?: FleetOnboardingMode
  selectedVertical?: FleetVertical
  units: FleetOperatingUnitDraft[]
}

export const FLEET_ONBOARDING_STORAGE_KEY = "fleet-b2b-onboarding-draft-v1"

export const FLEET_VERTICAL_LABELS: Record<FleetVertical, string> = {
  intercity_bus: "Inter-city Bus",
  trucking: "Trucking / Logistics",
  two_wheeler_delivery: "Two-wheeler Delivery",
}

export const FLEET_VERTICAL_DESCRIPTIONS: Record<FleetVertical, string> = {
  intercity_bus:
    "Long-haul passenger operations with route, depot, crew, and passenger-facing sticker needs.",
  trucking:
    "Truck and logistics fleets with branch, asset, helper, corridor, and cargo-driven safety operations.",
  two_wheeler_delivery:
    "Food, grocery, and rider-network operators with hub, zone, rider, and bike assignment workflows.",
}

export const FLEET_ONBOARDING_MODE_LABELS: Record<FleetOnboardingMode, string> = {
  import: "Upload Workbook",
  manual: "Manual Entry",
}

export const FLEET_ONBOARDING_MODE_DESCRIPTIONS: Record<FleetOnboardingMode, string> = {
  import:
    "Use the enterprise workbook for large customer onboarding with validation and preview before import.",
  manual:
    "Create a smaller pilot or branch roster directly in Fleet without waiting for a workbook upload.",
}

export const FLEET_VERTICAL_KIT_TEMPLATE_LABELS: Record<FleetVertical, string> = {
  intercity_bus: "Passenger coverage bus kit",
  trucking: "Standard truck issue kit",
  two_wheeler_delivery: "Rider shift issue kit",
}

export const FLEET_VERTICAL_ASSET_LABELS: Record<FleetVertical, string> = {
  intercity_bus: "Bus",
  trucking: "Truck",
  two_wheeler_delivery: "Two-wheeler",
}

export const FLEET_VERTICAL_PRIMARY_OPERATOR_LABELS: Record<FleetVertical, string> = {
  intercity_bus: "Primary driver",
  trucking: "Primary driver",
  two_wheeler_delivery: "Rider",
}

export const FLEET_VERTICAL_SECONDARY_OPERATOR_LABELS: Record<FleetVertical, string> = {
  intercity_bus: "Conductor / attendant",
  trucking: "Helper / co-driver",
  two_wheeler_delivery: "Backup rider / supervisor",
}

export const FLEET_VERTICAL_ROUTE_LABELS: Record<FleetVertical, string> = {
  intercity_bus: "Route / city pair",
  trucking: "Corridor / route",
  two_wheeler_delivery: "Hub / zone",
}

export const FLEET_VERTICAL_NOTES_LABELS: Record<FleetVertical, string> = {
  intercity_bus: "Passenger or service notes",
  trucking: "Cargo or operating notes",
  two_wheeler_delivery: "Delivery or shift notes",
}

export const FLEET_ONBOARDING_STEP_LABELS: Record<FleetOnboardingStep, string> = {
  import: "Import",
  manual: "Manual Entry",
  organization: "Organization",
  overview: "Onboarding",
  review: "Review",
}

export function createInitialFleetImportBridgeDraft(): FleetImportBridgeDraft {
  return {
    assetCount: 0,
    assignmentCount: 0,
    blockingErrors: [],
    dryRunCreated: 0,
    dryRunFailed: 0,
    dryRunRows: [],
    dryRunTotal: 0,
    dryRunUpdated: 0,
    fileName: "",
    headers: [],
    lastDryRunAt: null,
    operatingUnitHintCount: 0,
    peopleCount: 0,
    sourceRows: [],
    warnings: [],
  }
}

export function createInitialFleetKitIssuanceDraft(): FleetKitIssuanceDraft {
  return {
    activationMode: "system",
    batchCode: "",
    batchLabel: "",
    kits: [],
    preparedAt: null,
    templateKey: "trucking_standard",
    templateLabel: "",
    totalAssets: 0,
    totalStickers: 0,
    vertical: "trucking",
  }
}

export function createFleetOperatingUnitDraft(
  overrides: Partial<FleetOperatingUnitDraft> = {}
): FleetOperatingUnitDraft {
  return {
    city: "",
    code: "",
    escalationPhone: "",
    id:
      overrides.id
      || `unit-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`,
    isPrimary: false,
    managerName: "",
    managerPhone: "",
    name: "",
    region: "",
    type: "branch",
    ...overrides,
  }
}

export function createFleetManualAssetDraft(
  overrides: Partial<FleetManualAssetDraft> = {}
): FleetManualAssetDraft {
  return {
    assetNumber: "",
    id:
      overrides.id
      || `asset-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`,
    notes: "",
    operatingUnitId: "",
    primaryOperatorName: "",
    primaryOperatorPhone: "",
    routeOrZone: "",
    secondaryOperatorName: "",
    secondaryOperatorPhone: "",
    ...overrides,
  }
}

export function createInitialFleetOnboardingDraft(): FleetOnboardingDraft {
  return {
    activeOrganizationId: null,
    activeRunId: null,
    adminEmail: "",
    adminName: "",
    adminPhone: "",
    importBridge: null,
    kitIssuance: null,
    legalName: "",
    manualAssetCount: "",
    manualAssets: [],
    masterPersistence: null,
    operatingRegions: "",
    organizationName: "",
    preferredDriverLanguage: "English",
    preferredPublicLanguage: "English",
    primaryCity: "",
    primaryCountry: "India",
    selectedMode: "manual",
    selectedVertical: "trucking",
    units: [
      createFleetOperatingUnitDraft({
        code: "HQ-001",
        isPrimary: true,
        name: "Primary Branch",
        type: "branch",
      }),
    ],
  }
}

function readFleetOnboardingDraft() {
  if (typeof window === "undefined") {
    return createInitialFleetOnboardingDraft()
  }

  const raw = window.sessionStorage.getItem(FLEET_ONBOARDING_STORAGE_KEY)
  if (!raw) {
    return createInitialFleetOnboardingDraft()
  }

  try {
    const parsed = JSON.parse(raw) as Partial<FleetOnboardingDraft>
    const nextDraft = {
      ...createInitialFleetOnboardingDraft(),
      ...parsed,
      importBridge: parsed.importBridge
        ? {
            ...createInitialFleetImportBridgeDraft(),
            ...parsed.importBridge,
            dryRunRows: Array.isArray(parsed.importBridge.dryRunRows)
              ? parsed.importBridge.dryRunRows.map((row) => ({
                  error: row.error || null,
                  plateNumber: row.plateNumber || null,
                  rowIndex: Number(row.rowIndex) || 0,
                  status: String(row.status || ""),
                  tag: row.tag || null,
                }))
              : [],
            sourceRows: Array.isArray(parsed.importBridge.sourceRows)
              ? parsed.importBridge.sourceRows.map((row) =>
                  Object.fromEntries(
                    Object.entries(row || {}).map(([key, value]) => [String(key), String(value ?? "")])
                  )
                )
              : [],
          }
        : null,
      kitIssuance: parsed.kitIssuance
        ? {
            ...createInitialFleetKitIssuanceDraft(),
            ...parsed.kitIssuance,
            kits: Array.isArray(parsed.kitIssuance.kits)
              ? parsed.kitIssuance.kits.map((kit) => ({
                  assetNumber: String(kit.assetNumber || ""),
                  assetTypeLabel: String(kit.assetTypeLabel || ""),
                  operatingUnitLabel: String(kit.operatingUnitLabel || ""),
                  operatorLabel: String(kit.operatorLabel || ""),
                  routeOrZone: String(kit.routeOrZone || ""),
                  slots: Array.isArray(kit.slots)
                    ? kit.slots.map((slot) => ({
                        label: String(slot.label || ""),
                        previewCode: String(slot.previewCode || ""),
                        scanFlow: String(slot.scanFlow || ""),
                        slotKey: String(slot.slotKey || ""),
                        visibility: slot.visibility === "private" ? "private" : "public",
                      }))
                    : [],
                }))
              : [],
          }
        : null,
      masterPersistence: parsed.masterPersistence
        ? {
            assignmentCount: Number(parsed.masterPersistence.assignmentCount) || 0,
            assetCount: Number(parsed.masterPersistence.assetCount) || 0,
            lastPersistedAt: parsed.masterPersistence.lastPersistedAt || null,
            onboardingMode: parsed.masterPersistence.onboardingMode === "import" ? "import" : "manual",
            organizationId: String(parsed.masterPersistence.organizationId || ""),
            peopleCount: Number(parsed.masterPersistence.peopleCount) || 0,
            runId: parsed.masterPersistence.runId
              ? String(parsed.masterPersistence.runId)
              : null,
            unitCount: Number(parsed.masterPersistence.unitCount) || 0,
            vertical: FLEET_VERTICAL_OPTIONS.includes(parsed.masterPersistence.vertical as FleetVertical)
              ? (parsed.masterPersistence.vertical as FleetVertical)
              : "trucking",
          }
        : null,
      manualAssets: Array.isArray(parsed.manualAssets)
        ? parsed.manualAssets.map((asset) => createFleetManualAssetDraft(asset))
        : [],
      units: Array.isArray(parsed.units) && parsed.units.length
        ? parsed.units.map((unit, index) =>
            createFleetOperatingUnitDraft({
              ...unit,
              isPrimary: index === 0 ? true : Boolean(unit.isPrimary),
            })
          )
        : createInitialFleetOnboardingDraft().units,
      selectedMode: FLEET_ONBOARDING_MODE_OPTIONS.includes(parsed.selectedMode as FleetOnboardingMode)
        ? (parsed.selectedMode as FleetOnboardingMode)
        : createInitialFleetOnboardingDraft().selectedMode,
      selectedVertical: FLEET_VERTICAL_OPTIONS.includes(parsed.selectedVertical as FleetVertical)
        ? (parsed.selectedVertical as FleetVertical)
        : createInitialFleetOnboardingDraft().selectedVertical,
    } satisfies FleetOnboardingDraft

    if (!nextDraft.units.some((unit) => unit.isPrimary) && nextDraft.units[0]) {
      nextDraft.units[0] = {
        ...nextDraft.units[0],
        isPrimary: true,
      }
    }

    return nextDraft
  } catch {
    return createInitialFleetOnboardingDraft()
  }
}

export function useFleetOnboardingDraft() {
  const [draft, setDraftState] = useState<FleetOnboardingDraft>(() => readFleetOnboardingDraft())
  const draftRef = useRef(draft)

  useEffect(() => {
    draftRef.current = draft
  }, [draft])

  const persistDraft = useCallback((nextDraft: FleetOnboardingDraft) => {
    draftRef.current = nextDraft

    if (typeof window !== "undefined") {
      window.sessionStorage.setItem(FLEET_ONBOARDING_STORAGE_KEY, JSON.stringify(nextDraft))
    }

    setDraftState(nextDraft)
  }, [])

  const setDraft = useCallback((
    nextDraft:
      | FleetOnboardingDraft
      | ((previous: FleetOnboardingDraft) => FleetOnboardingDraft)
  ) => {
    const resolvedDraft =
      typeof nextDraft === "function"
        ? nextDraft(draftRef.current)
        : nextDraft

    persistDraft(resolvedDraft)
  }, [persistDraft])

  useEffect(() => {
    if (typeof window === "undefined") {
      return
    }

    window.sessionStorage.setItem(FLEET_ONBOARDING_STORAGE_KEY, JSON.stringify(draft))
  }, [draft])

  return {
    draft,
    resetDraft() {
      const nextDraft = createInitialFleetOnboardingDraft()
      persistDraft(nextDraft)
    },
    setDraft,
  }
}

export function normalizeFleetOnboardingStep(
  step: FleetOnboardingStep | undefined,
  mode: FleetOnboardingMode | undefined
) {
  if (step) {
    return step
  }

  if (mode === "import") {
    return "import"
  }

  if (mode === "manual") {
    return "manual"
  }

  return "overview"
}

function inferFleetOnboardingModeFromDraft(
  draft: Pick<
    FleetOnboardingDraft,
    "importBridge" | "manualAssetCount" | "manualAssets" | "masterPersistence" | "selectedMode"
  >
): FleetOnboardingMode {
  if (draft.selectedMode && FLEET_ONBOARDING_MODE_OPTIONS.includes(draft.selectedMode)) {
    return draft.selectedMode
  }

  if (draft.masterPersistence?.onboardingMode) {
    return draft.masterPersistence.onboardingMode
  }

  const hasImportSignal = Boolean(
    draft.importBridge?.fileName.trim()
      || draft.importBridge?.lastDryRunAt
      || draft.importBridge?.sourceRows.length
  )

  if (hasImportSignal) {
    return "import"
  }

  const hasManualSignal = Boolean(
    draft.manualAssetCount.trim() || draft.manualAssets.length
  )
  if (hasManualSignal) {
    return "manual"
  }

  return "manual"
}

export function resolveFleetOnboardingMode(
  mode: FleetOnboardingMode | undefined,
  draft: Pick<
    FleetOnboardingDraft,
    "importBridge" | "manualAssetCount" | "manualAssets" | "masterPersistence" | "selectedMode"
  >,
  routeHint?: FleetOnboardingStep
): FleetOnboardingMode {
  if (routeHint === "manual") {
    return "manual"
  }
  if (routeHint === "import") {
    return "import"
  }
  if (mode) {
    return mode
  }

  return inferFleetOnboardingModeFromDraft(draft)
}

export function resolveFleetOnboardingVertical(
  vertical: FleetVertical | undefined,
  draft: Pick<FleetOnboardingDraft, "kitIssuance" | "masterPersistence" | "selectedVertical">
): FleetVertical {
  if (vertical) {
    return vertical
  }

  if (draft.selectedVertical && FLEET_VERTICAL_OPTIONS.includes(draft.selectedVertical)) {
    return draft.selectedVertical
  }

  if (draft.masterPersistence?.vertical) {
    return draft.masterPersistence.vertical
  }

  if (draft.kitIssuance?.vertical) {
    return draft.kitIssuance.vertical
  }

  return "trucking"
}

export function applyFleetOnboardingSelection(
  draft: FleetOnboardingDraft,
  selection: Partial<Pick<FleetOnboardingDraft, "selectedMode" | "selectedVertical">>
): FleetOnboardingDraft {
  const nextMode = selection.selectedMode ?? draft.selectedMode
  const nextVertical = selection.selectedVertical ?? draft.selectedVertical
  const currentMode = inferFleetOnboardingModeFromDraft(draft)
  const modeChanged = Boolean(nextMode && nextMode !== currentMode)
  const verticalChanged = Boolean(nextVertical && nextVertical !== resolveFleetOnboardingVertical(undefined, draft))

  if (!modeChanged && !verticalChanged) {
    return {
      ...draft,
      selectedMode: nextMode,
      selectedVertical: nextVertical,
    }
  }

  const nextDraft: FleetOnboardingDraft = {
    ...draft,
    kitIssuance: null,
    masterPersistence: null,
    selectedMode: nextMode,
    selectedVertical: nextVertical,
  }

  if (modeChanged && nextMode === "manual") {
    nextDraft.importBridge = null
  }

  if (modeChanged && nextMode === "import") {
    nextDraft.manualAssetCount = ""
    nextDraft.manualAssets = []
  }

  return nextDraft
}

export function applyFleetOnboardingRouteContext(
  draft: FleetOnboardingDraft,
  context: Partial<{
    mode: FleetOnboardingMode
    organizationId: string
    runId: string
    vertical: FleetVertical
  }>
): FleetOnboardingDraft {
  const nextDraft =
    context.mode || context.vertical
      ? applyFleetOnboardingSelection(draft, {
          selectedMode: context.mode,
          selectedVertical: context.vertical,
        })
      : draft

  const normalizedOrganizationId = context.organizationId?.trim() || null
  const normalizedRunId = context.runId?.trim() || null
  const organizationChanged = normalizedOrganizationId !== null
    && normalizedOrganizationId !== nextDraft.activeOrganizationId
  const runChanged = normalizedRunId !== null && normalizedRunId !== nextDraft.activeRunId

  if (!organizationChanged && !runChanged) {
    return nextDraft
  }

  return {
    ...nextDraft,
    activeOrganizationId: organizationChanged
      ? normalizedOrganizationId
      : nextDraft.activeOrganizationId,
    activeRunId: runChanged ? normalizedRunId : nextDraft.activeRunId,
  }
}

export function summarizeFleetOnboardingDraft(draft: FleetOnboardingDraft) {
  const completedOrganizationFields = [
    draft.organizationName,
    draft.legalName,
    draft.adminName,
    draft.adminEmail,
    draft.adminPhone,
    draft.primaryCity,
  ].filter((value) => value.trim()).length

  const completeUnits = draft.units.filter((unit) => {
    return unit.code.trim() && unit.name.trim() && unit.city.trim()
  }).length

  return {
    completedOrganizationFields,
    completeUnits,
    importDryRunReady: Boolean(draft.importBridge?.lastDryRunAt),
    issuedDraftAssets: draft.kitIssuance?.totalAssets || 0,
    issuedDraftStickers: draft.kitIssuance?.totalStickers || 0,
    masterPersistenceReady: Boolean(draft.masterPersistence?.lastPersistedAt),
    manualReadyAssets: draft.manualAssets.filter((asset) => {
      return asset.assetNumber.trim() && asset.operatingUnitId.trim()
    }).length,
    totalManualAssets: draft.manualAssets.length,
    totalUnits: draft.units.length,
  }
}

function compactAssetToken(value: string) {
  return value.replace(/[^A-Z0-9]/gi, "").toUpperCase().slice(-8) || "ASSET"
}

export function getFleetKitTemplate(vertical: FleetVertical): {
  key: FleetKitTemplateKey
  label: string
  slots: FleetKitTemplateSlot[]
} {
  if (vertical === "intercity_bus") {
    return {
      key: "intercity_bus_passenger",
      label: FLEET_VERTICAL_KIT_TEMPLATE_LABELS[vertical],
      slots: [
        { label: "Public entry", scanFlow: "public_report", slotKey: "public_entry", visibility: "public" },
        { label: "Public exit", scanFlow: "public_report", slotKey: "public_exit", visibility: "public" },
        { label: "Passenger cabin", scanFlow: "public_report", slotKey: "passenger_cabin", visibility: "public" },
        { label: "Private driver", scanFlow: "driver_private", slotKey: "private_driver", visibility: "private" },
        { label: "Private conductor", scanFlow: "conductor_private", slotKey: "private_conductor", visibility: "private" },
      ],
    }
  }

  if (vertical === "two_wheeler_delivery") {
    return {
      key: "two_wheeler_delivery_shift",
      label: FLEET_VERTICAL_KIT_TEMPLATE_LABELS[vertical],
      slots: [
        { label: "Public vehicle", scanFlow: "public_report", slotKey: "public_primary", visibility: "public" },
        { label: "Private rider", scanFlow: "rider_private", slotKey: "private_rider", visibility: "private" },
        { label: "Backup rider", scanFlow: "backup_private", slotKey: "private_backup", visibility: "private" },
        { label: "Hub support", scanFlow: "hub_private", slotKey: "hub_support", visibility: "private" },
      ],
    }
  }

  return {
    key: "trucking_standard",
    label: FLEET_VERTICAL_KIT_TEMPLATE_LABELS[vertical],
    slots: [
      { label: "Public primary", scanFlow: "public_report", slotKey: "public_primary", visibility: "public" },
      { label: "Public secondary", scanFlow: "public_report", slotKey: "public_secondary", visibility: "public" },
      { label: "Private driver", scanFlow: "driver_private", slotKey: "private_driver", visibility: "private" },
      { label: "Private helper", scanFlow: "helper_private", slotKey: "private_helper", visibility: "private" },
    ],
  }
}

export function buildFleetKitIssuanceDraft(
  draft: FleetOnboardingDraft,
  vertical: FleetVertical
): FleetKitIssuanceDraft | null {
  const template = getFleetKitTemplate(vertical)
  const primaryUnit = draft.units.find((unit) => unit.isPrimary) || draft.units[0]
  const preparedAt = new Date().toISOString()
  const batchCode = `KIT-${Date.now().toString(36).toUpperCase()}`

  const manualSource = draft.manualAssets
    .filter((asset) => asset.assetNumber.trim() && asset.operatingUnitId.trim())
    .map((asset) => {
      const operatingUnit = draft.units.find((unit) => unit.id === asset.operatingUnitId)
      return {
        assetNumber: asset.assetNumber.trim().toUpperCase(),
        operatingUnitLabel: operatingUnit
          ? `${operatingUnit.code || "UNIT"} • ${operatingUnit.name || operatingUnit.city}`
          : primaryUnit
            ? `${primaryUnit.code || "UNIT"} • ${primaryUnit.name || primaryUnit.city}`
            : "Primary operating unit",
        operatorLabel: asset.primaryOperatorName.trim() || "Unassigned operator",
        routeOrZone: asset.routeOrZone.trim() || draft.operatingRegions.trim() || "Route not captured",
      }
    })

  const importSource = draft.importBridge?.lastDryRunAt
    ? draft.importBridge.dryRunRows
        .filter((row) => row.status !== "failed")
        .map((row) => ({
          assetNumber: (row.plateNumber || row.tag || `ROW-${row.rowIndex}`).trim().toUpperCase(),
          operatingUnitLabel: primaryUnit
            ? `${primaryUnit.code || "UNIT"} • ${primaryUnit.name || primaryUnit.city}`
            : "Imported operating unit",
          operatorLabel: "Imported assignment",
          routeOrZone: draft.operatingRegions.trim() || "Imported route context",
        }))
    : []

  const sourceAssets = manualSource.length > 0 ? manualSource : importSource
  if (sourceAssets.length === 0) {
    return null
  }

  const kits = sourceAssets.map((asset) => ({
    assetNumber: asset.assetNumber,
    assetTypeLabel: FLEET_VERTICAL_ASSET_LABELS[vertical],
    operatingUnitLabel: asset.operatingUnitLabel,
    operatorLabel: asset.operatorLabel,
    routeOrZone: asset.routeOrZone,
    slots: template.slots.map((slot, index) => ({
      ...slot,
      previewCode: `${compactAssetToken(asset.assetNumber)}-${String(index + 1).padStart(2, "0")}-${slot.slotKey.replace(/[^A-Z0-9]/gi, "").toUpperCase().slice(0, 6)}`,
    })),
  }))

  return {
    activationMode: "system",
    batchCode,
    batchLabel: `${draft.organizationName.trim() || "Fleet"} ${template.label}`,
    kits,
    preparedAt,
    templateKey: template.key,
    templateLabel: template.label,
    totalAssets: kits.length,
    totalStickers: kits.reduce((sum, kit) => sum + kit.slots.length, 0),
    vertical,
  }
}

export interface FleetReviewReadiness {
  blockingIssues: string[]
  warnings: string[]
}

export function getFleetReviewReadiness(
  draft: FleetOnboardingDraft,
  mode: FleetOnboardingMode,
  vertical: FleetVertical
): FleetReviewReadiness {
  const blockingIssues: string[] = []
  const warnings: string[] = []
  const summary = summarizeFleetOnboardingDraft(draft)
  const assetLabel = FLEET_VERTICAL_ASSET_LABELS[vertical]

  const requiredOrganizationFields = [
    ["organization name", draft.organizationName],
    ["legal name", draft.legalName],
    ["primary admin name", draft.adminName],
    ["primary admin email", draft.adminEmail],
    ["primary admin phone", draft.adminPhone],
    ["primary city", draft.primaryCity],
  ] as const

  const missingOrganizationFields = requiredOrganizationFields
    .filter(([, value]) => !value.trim())
    .map(([label]) => label)

  if (missingOrganizationFields.length > 0) {
    blockingIssues.push(
      `Complete the organization profile before issuance. Missing: ${missingOrganizationFields.join(", ")}.`
    )
  }

  if (summary.completeUnits === 0) {
    blockingIssues.push(
      "Add at least one complete operating unit with code, name, and city before issuing kits."
    )
  } else if (summary.completeUnits < summary.totalUnits) {
    warnings.push(
      `${summary.totalUnits - summary.completeUnits} operating unit draft(s) are still incomplete.`
    )
  }

  if (!draft.operatingRegions.trim()) {
    warnings.push("Operating regions are still blank. Corridor or zone context will be useful for enterprise rollout.")
  }

  if (mode === "manual") {
    if (draft.manualAssets.length === 0) {
      blockingIssues.push(
        `Generate at least one ${assetLabel.toLowerCase()} draft row before the review can continue.`
      )
    } else if (summary.manualReadyAssets < draft.manualAssets.length) {
      blockingIssues.push(
        `${draft.manualAssets.length - summary.manualReadyAssets} ${assetLabel.toLowerCase()} row(s) still need the required asset number or operating-unit mapping.`
      )
    }
  }

  if (mode === "import") {
    if (!draft.importBridge?.fileName) {
      blockingIssues.push("Upload a fleet CSV file before final review can continue.")
    } else if (draft.importBridge.blockingErrors.length > 0) {
      blockingIssues.push("Resolve the CSV validation errors before continuing from review.")
    }

    if (!draft.importBridge?.lastDryRunAt) {
      blockingIssues.push("Validate and save upload rows before continuing from the review step.")
    } else {
      if (draft.importBridge.dryRunTotal === 0) {
        blockingIssues.push("The saved upload returned zero rows. Review the file before continuing.")
      }
      if (draft.importBridge.dryRunFailed > 0) {
        blockingIssues.push(
          `The saved upload reported ${draft.importBridge.dryRunFailed} failed row(s). Resolve them before continuing.`
        )
      }
      if (draft.importBridge.warnings.length > 0) {
        warnings.push(...draft.importBridge.warnings)
      }
    }
  }

  return {
    blockingIssues,
    warnings,
  }
}
